import React, { Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { apiOrganization } from "src/services/Api/Organization/apiOrganization";
import { wrapPromise } from "go-core";
import Header from "go-app/components/Header";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { OrganizationState } from "go-security/services/organizations/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext, SegmentListService } from "go-segment/context";
import { ProductGroupsApi } from "../../../../../../../../../../services/Api/Organization/types";

interface ListState {
	resource?: Record<string, any>;
}

const listName = "PRODUCT_GROUPS";
const resourceType = "PRODUCT_GROUPS";

const buildConfig = (
	t: TFunction,
	data: Record<string, any>,
	organization: OrganizationState,
	segmentListService: SegmentListService
) => {
	let config = {
		fields: [
			{
				id: "name",
				name: t("common.word.name", { ns: "lib" }),
				render: (item: ProductGroupsApi) => (
					<Link to={`/${organization.id}/products/${item.id}/edit`} rel="noreferrer" target="_blank">
						{item.name}
					</Link>
				),
				renderExport: (item: ProductGroupsApi) => item.name,
			},
			{
				id: "recipe_amount",
				name: t("common.word.quantity"),
			},
			{
				id: "measure_type",
				name: t("common.word.measure_type"),
				type: "list",
				listOptions: {
					ITEM: t("enums.products.measure_types.ITEM"),
					KILOGRAM: t("enums.products.measure_types.KILOGRAM"),
					LITER: t("enums.products.measure_types.LITER"),
					PACKAGE: t("enums.products.measure_types.PACKAGE"),
				},
				render: (item: ProductGroupsApi) => t(`enums.products.measure_types.${item.measure_type}`),
			},
			{
				id: "category",
				name: t("common.word.category"),
				render: (item: ProductGroupsApi) => item.category_name,
			},
			{
				id: "product_cost_price_money",
				name: t("modules.product_group.field.cost_price_money.title"),
				render: (item: ProductGroupsApi) =>
					`${FormatMoney(item.product_cost_price_money)} (${FormatMoney(
						item.product_cost_price_gross_money
					)})`,
			},
			{
				id: "reference_price_money",
				name: t("modules.product_group.field.default_price_net_money.title"),
				render: (item: ProductGroupsApi) =>
					`${FormatMoney(item.reference_price_money)} (${FormatMoney(item.reference_price_gross_money)})`,
				disableSorting: true,
			},
			{
				id: "last_price_money",
				name: t("modules.product_group.field.last_price_money.title"),
				render: (item: ProductGroupsApi) =>
					`${FormatMoney(item.last_price_money)} (${FormatMoney(item.last_price_gross_money)})`,
				disableSorting: true,
			},
		],
		nested_config: {
			field: "components",
			withExpandAllButton: true,
		},
		selectedColumns: [
			"name",
			"recipe_amount",
			"measure_type",
			"category",
			"product_cost_price_money",
			"reference_price_money",
			"last_price_money",
		],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
			},
			{
				id: "deleted",
				name: t("go_list.filters.deleted", { ns: "lib" }),
				slug: "deleted",
				filters: [
					{
						filterId: "statuses",
						value: "DELETED",
						condition: "e",
					},
				],
			},
		],
		filters: [
			{
				id: "products",
				name: t("common.word.product"),
				type: "search_select",
				source: {
					request: async (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getProductSearchSelect(search, params, { cancelToken: options?.cancelToken }),
				},
			},
			{
				id: "productsInRecipe",
				name: t("modules.product_group.field.products_in_recipe.title"),
				type: "search_select",
				source: {
					request: async (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getProductSearchSelect(search, params, { cancelToken: options?.cancelToken }),
				},
			},
			{
				id: "categories",
				name: t("common.word.category"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) => {
						return apiOrganization.getProductCategorySearchSelect(search, params, {
							cancelToken: options?.cancelToken,
						});
					},
				},
			},
			{
				id: "productType",
				name: t("common.word.type", { ns: "lib" }),
				type: "list",
				listOptions: {
					BASIC: t("enums.product.type.BASIC"),
					PACKAGE: t("enums.product.type.PACKAGE"),
				},
			},
			{
				id: "sku",
				name: t("common.word.sku"),
				type: "text",
			},
			{
				id: "description",
				name: t("common.word.description", { ns: "lib" }),
				type: "text",
			},
			{
				id: "statuses",
				name: t("common.word.status", { ns: "lib" }),
				type: "list",
				listOptions: {
					ENABLED: t("enums.common.status.ENABLED", { ns: "lib" }),
					DISABLED: t("enums.common.status.DISABLED", { ns: "lib" }),
					DELETED: t("enums.common.status.DELETED", { ns: "lib" }),
				},
			},
		],
		exportConfig: {
			title: t("modules.product_group.header.title"),
			filename: t("modules.product_group.field.export_config.file_name"),
			organization: organization.name,
		},
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			const newParams = { ...params, include: "custom_fields" };
			return apiOrganization.getProductsGroups(newParams, { cancelToken: sourceToken?.token });
		},
		saveSegment: async (segment: SegmentType) => segmentListService.save(listName, resourceType, segment),
	} as ListConfig;
	config = {
		...config,
		externalSegments: data.segments,
		filterValues: data.filter_values,
	};
	return config;
};

const List = ({ resource }: ListState) => {
	const segmentContextValue = useContext(SegmentContext);
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);

	if (!resource) return null;

	const data = resource.read();
	const config = buildConfig(t, data, organization, segmentContextValue);

	return <ListData config={config} />;
};

const OrganizationProductGroupsIndexPage = () => {
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = useContext(SegmentContext);
	const { t } = useTranslation();
	const customFieldResourceTypes = [
		{
			type: "PRODUCT",
			name: t(`enums.custom_fields.resources.PRODUCT`),
		},
	];
	const { isMobile } = useWindowSize();
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(t("modules.product_group.header.title"));
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType, customFieldResourceTypes)));
	}, []);

	return (
		<>
			{!isMobile && <Header title={t("modules.product_group.header.title")} />}
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} />
			</Suspense>
		</>
	);
};
export default OrganizationProductGroupsIndexPage;
